import { useCallback, useRef, useState } from 'react';

import DatePicker from '../../../../components/DatePicker';
import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { FormList, FormListFieldError, FormListItem } from '../../../../components/FormList';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { WorkflowSubStepProps } from '../../config';
import './CurrentViolationsPage.css';

export interface ViolationEntry {
  violationDate: string;
  violationDetails: string;
}

export interface CurrentViolationsFormData {
  isAddingEntry?: boolean;
  hasCurrentViolations: boolean | null;
  violations: FormListItem<ViolationEntry>[];
  draftViolation: ViolationEntry;
}

const yesNoOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const getEmptyViolation = (): ViolationEntry => ({
  violationDate: '',
  violationDetails: '',
});

function CurrentViolationsPage({ data, fieldErrors, updateData }: WorkflowSubStepProps<CurrentViolationsFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'current-violations-page';

  const today = new Date();
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 10);

  const handleViolationsChange = (value: boolean | null) => {
    if (value !== true) {
      updateData({
        ...data,
        draftViolation: getEmptyViolation(),
        hasCurrentViolations: value,
        isAddingEntry: false,
        violations: [],
      });
    } else {
      updateData({
        ...data,
        hasCurrentViolations: value,
      });
    }
  };

  const pendingItemsRef = useRef<FormListItem<ViolationEntry>[] | null>(null);

  const handleItemsChange = useCallback(
    (items: FormListItem<ViolationEntry>[]) => {
      pendingItemsRef.current = items;
      updateData({
        ...data,
        violations: items,
      });
    },
    [data, updateData],
  );

  const handleDraftChange = useCallback(
    (draft: ViolationEntry, isDraftActive?: boolean) => {
      const itemsToUse = pendingItemsRef.current !== null ? pendingItemsRef.current : data.violations;
      pendingItemsRef.current = null;

      updateData({
        ...data,
        draftViolation: draft,
        isAddingEntry: isDraftActive ?? data.isAddingEntry,
        violations: itemsToUse,
      });
    },
    [data, updateData],
  );

  const handleDraftActiveChange = useCallback(
    (isActive: boolean) => updateData({ ...data, isAddingEntry: isActive }),
    [data, updateData],
  );

  const validateViolation = useCallback((item: ViolationEntry): FormListFieldError => {
    const errors: FormListFieldError = {};

    if (!item.violationDate) {
      errors.violationDate = 'Date of the violation is required';
    }
    if (!item.violationDetails) {
      errors.violationDetails = 'Explanation of the violation is required';
    }

    return errors;
  }, []);

  const isDraftEmpty = useCallback((draft: ViolationEntry): boolean => {
    return !draft.violationDate && !draft.violationDetails;
  }, []);

  const renderEditForm = useCallback(
    (item: ViolationEntry, onChange: (item: ViolationEntry) => void, errors: FormListFieldError) => {
      const handleFieldChange = <K extends keyof ViolationEntry>(field: K, value: ViolationEntry[K]) => {
        onChange({
          ...item,
          [field]: value,
        });
      };

      return (
        <div
          className='form-list__nested-form'
          data-testid={`${baseTestId}-entry-form`}>
          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-entry-date`}>
            <DatePicker
              required
              data-testid={`${baseTestId}-entry-date-picker`}
              errorMessage={errors.violationDate}
              fromDate={minDate}
              id='violationDate'
              label='Date of the violation'
              name='violationDate'
              toDate={today}
              tooltipText='Enter the date of the violation'
              value={item.violationDate || ''}
              onDateChange={(date, formattedDate) => handleFieldChange('violationDate', formattedDate || '')}
            />
          </div>

          <div
            className='ds-u-margin-bottom--3'
            data-testid={`${baseTestId}-entry-details`}>
            <TextField
              multiline
              required
              data-testid={`${baseTestId}-entry-details-field`}
              errorMessage={errors.violationDetails}
              label='Explanation of the violation'
              name='violationDetails'
              rows={5}
              tooltipText='Provide details including an explanation of the violation, place of occurrence, and the name and address of the police department or court involved'
              value={item.violationDetails || ''}
              onChange={(e) => handleFieldChange('violationDetails', e.target.value)}
            />
          </div>
        </div>
      );
    },
    [minDate, today],
  );

  const renderItemSummary = useCallback((item: ViolationEntry) => {
    const truncatedDetails =
      item.violationDetails.length > 50 ? `${item.violationDetails.substring(0, 50)}...` : item.violationDetails;

    return {
      details: truncatedDetails,
      title: item.violationDate,
    };
  }, []);

  const renderReadOnlyItem = useCallback((item: ViolationEntry) => {
    return (
      <div className='form-list__nested-form'>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Date</span>
          <span className='form-list__readonly-value'>{item.violationDate}</span>
        </div>
        <div className='form-list__readonly-field'>
          <span className='form-list__readonly-label'>Details</span>
          <span className='form-list__readonly-value'>{item.violationDetails}</span>
        </div>
      </div>
    );
  }, []);

  const draftFieldErrors: FormListFieldError = {
    violationDate: fieldErrors?.['draftViolation.violationDate'],
    violationDetails: fieldErrors?.['draftViolation.violationDetails'],
  };

  return (
    <div
      className='current-violations-page'
      data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-has-current-violations`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-has-current-violations-dropdown`}
          errorMessage={fieldErrors?.hasCurrentViolations}
          label='Are you currently under charges for any violation of the law?'
          name='hasCurrentViolations'
          options={yesNoOptions}
          tooltipText='This question asks about any current pending charges for violations of the law.'
          value={data.hasCurrentViolations}
          onChange={handleViolationsChange}
        />
      </div>

      {data.hasCurrentViolations === true && (
        <div
          className='ds-u-margin-bottom--3'
          data-testid={`${baseTestId}-entries`}>
          <FormList<ViolationEntry>
            addLinkText='Add Another Violation'
            data-testid={`${baseTestId}-entries-list`}
            draftItem={data.draftViolation}
            fieldErrors={draftFieldErrors}
            getEmptyItem={getEmptyViolation}
            isDraftActive={data.isAddingEntry}
            isDraftEmpty={isDraftEmpty}
            items={data.violations}
            label='Violation Details'
            renderEditForm={renderEditForm}
            renderItemSummary={renderItemSummary}
            renderReadOnlyItem={renderReadOnlyItem}
            tooltipText='Provide details about each current violation charge.'
            validateItem={validateViolation}
            onDraftActiveChange={handleDraftActiveChange}
            onDraftChange={handleDraftChange}
            onItemsChange={handleItemsChange}
          />
        </div>
      )}

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Current Violations'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Law Violations Details</h3>
          <p>
            This section asks about any current charges for violations of the law. This is required for the Declaration
            of Federal Employment (OF-306).
          </p>

          <h4 className='ds-u-margin-top--3'>Are you currently under charges for any violation of the law?</h4>
          <p>Answer &quot;Yes&quot; if you have any charges for any violation of the law against you.</p>

          <h4 className='ds-u-margin-top--3'>Date of the violation</h4>
          <p>Enter the date of the violation or when charges were filed.</p>

          <h4 className='ds-u-margin-top--3'>Explanation of the violation</h4>
          <p>Enter details about the law violation. Describe the events that led to it.</p>
          {/* TODO: Restore the following guidance when ACP-790 is completed with these fields.
            <h4 className='ds-u-margin-top--3'>Place of the occurrence</h4>
          <p>Enter the name of the location where the event occurred.</p>
          <h4 className='ds-u-margin-top--3'>Name of the police department or court involved</h4>
          <p>
            Enter the name of the police department or court that was involved in the conviction, imprisonment,
            probation, or parole.
          </p>
          <h4 className='ds-u-margin-top--3'>Police Department or Court Address Line 1</h4>
          <p>Enter the address of the police department or court that was involved in the law violation.</p>
          <ul>
            <li>
              <strong>Address Line 1:</strong> Enter the police department or court&apos;s street address.
            </li>
            <li>
              <strong>Address Line 2:</strong> Enter the police department or court&apos;s suite, floor, or building
              number (if this applies).
            </li>
            <li>
              <strong>City:</strong> Enter the city name of the police department or court.
            </li>
            <li>
              <strong>State:</strong> Select the police department or court&apos;s state from the dropdown.
            </li>
            <li>
              <strong>Zip Code:</strong> Enter the police department or court&apos;s 5-digit ZIP code.
            </li>
          </ul>
          <h4 className='ds-u-margin-top--3'>Police Department or Court Address Line 2</h4>
          <p>See "Police Department or Court Address Line 1."</p>
          <h4 className='ds-u-margin-top--3'>Police Department or Court City</h4>
          <p>See "Police Department or Court Address Line 1."</p>
          <h4 className='ds-u-margin-top--3'>Police Department or Court Address State</h4>
          <p>See "Police Department or Court Address Line 1."</p>
          <h4 className='ds-u-margin-top--3'>Police Department or Court Address Zip Code</h4>
          <p>See "Police Department or Court Address Line 1."</p>*/}
          <p className='ds-u-margin-top--3'>
            <strong>Note:</strong> If you have more than one law violation to detail, use the &quot;Add Another
            Violation&quot; button to add each one.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default CurrentViolationsPage;
